import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@/components/shared/ui';
import { ChevronDown, ChevronRight, Check, GraduationCap, Monitor, X } from 'lucide-react';
import { Device } from '../../../types/admin';
import { useOrganizationDeviceAllocation } from '@/hooks/useLicense';
import { useRegisterClassDevice, useUnassignClassDevice } from '@/data/queries/useClasses';
import { parseUiDeviceIdToApiOrganizationDeviceId } from '@/utils/organizationDeviceId';

function findDeviceByOrgId(devs: Device[], organizationDeviceId: number): Device | undefined {
	return devs.find(
		(d) => parseUiDeviceIdToApiOrganizationDeviceId(String(d.id)) === organizationDeviceId
	);
}

export interface AssignClassToDeviceModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** 기관 ID — `GET /organization/{id}/device-allocation` 에 사용 */
	organizationId?: number;
	devices: Device[];
	/** 기기-클래스 연결 성공 시 (기기 목록 refetch 등) */
	onAssigned?: () => void;
}

export default function AssignClassToDeviceModal({
	isOpen,
	onClose,
	organizationId,
	devices,
	onAssigned,
}: AssignClassToDeviceModalProps): React.ReactElement {
	const { t } = useTranslation('admin');
	const oid = organizationId != null && Number.isFinite(organizationId) && organizationId > 0 ? organizationId : 0;
	const enabled = isOpen && oid > 0;

	const { data: allocData, isFetching: allocationLoading, dataUpdatedAt: allocationUpdatedAt } =
		useOrganizationDeviceAllocation(oid, { enabled });

	const registerMutation = useRegisterClassDevice();
	const unassignMutation = useUnassignClassDevice();

	/** 클래스별 `organizationDeviceId` — `GET /organization/{id}/device-allocation` + 모달에서 추가 */
	const [assignedByClass, setAssignedByClass] = useState<Record<number, number[]>>({});
	const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(new Set());
	const [expandedClassIds, setExpandedClassIds] = useState<Set<number>>(new Set());
	const [addingClassId, setAddingClassId] = useState<number | null>(null);
	const [selectedOrgDeviceIds, setSelectedOrgDeviceIds] = useState<Set<number>>(new Set());
	const [formError, setFormError] = useState<string>('');

	useEffect(() => {
		if (!isOpen) {
			setExpandedProductIds(new Set());
			setExpandedClassIds(new Set());
			setAssignedByClass({});
			setAddingClassId(null);
			setSelectedOrgDeviceIds(new Set());
			setFormError('');
		}
	}, [isOpen]);

	/** 서버 `device-allocation` 응답이 갱신될 때마다 클래스별 할당 id 동기화 */
	useEffect(() => {
		if (!isOpen) return;
		if (!allocData) return;
		setAssignedByClass({ ...allocData.byClassId });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- dataUpdatedAt 기준
	}, [isOpen, allocationUpdatedAt]);

	const classIds = useMemo(() => {
		const keys = Object.keys(assignedByClass).map((k) => Number(k));
		return keys
			.filter((n) => Number.isFinite(n) && n > 0)
			.sort((a, b) => a - b);
	}, [assignedByClass]);

	const productGroups = allocData?.productGroups;
	const hasProductTree = (productGroups?.length ?? 0) > 0;

	const activeDevices = useMemo(() => devices.filter((d) => d.status === 'active'), [devices]);

	const toggleProduct = (productId: number) => {
		setExpandedProductIds((prev) => {
			const next = new Set(prev);
			if (next.has(productId)) next.delete(productId);
			else next.add(productId);
			return next;
		});
	};

	const toggleClass = (classId: number) => {
		setExpandedClassIds((prev) => {
			const next = new Set(prev);
			if (next.has(classId)) next.delete(classId);
			else next.add(classId);
			return next;
		});
	};

	const startAddDevices = (classId: number) => {
		setAddingClassId(classId);
		setSelectedOrgDeviceIds(new Set());
		setFormError('');
	};

	const cancelAdd = () => {
		setAddingClassId(null);
		setSelectedOrgDeviceIds(new Set());
		setFormError('');
	};

	const toggleDeviceSelect = (orgDeviceId: number) => {
		setSelectedOrgDeviceIds((prev) => {
			const next = new Set(prev);
			if (next.has(orgDeviceId)) next.delete(orgDeviceId);
			else next.add(orgDeviceId);
			return next;
		});
	};

	/** 동일 `organizationDeviceId`는 다른 클래스에 중복 배정되지 않도록(서버 `device-allocation` 기준 + 로컬 갱신) */
	const eligibleDevicesForClass = useCallback(
		(classId: number): Device[] => {
			const usedElsewhere = new Set<number>();
			for (const [cidStr, ids] of Object.entries(assignedByClass)) {
				const cid = Number(cidStr);
				if (cid === classId) continue;
				for (const id of ids) usedElsewhere.add(id);
			}
			const usedThis = new Set(assignedByClass[classId] ?? []);
			return activeDevices.filter((d) => {
				const nid = parseUiDeviceIdToApiOrganizationDeviceId(String(d.id));
				if (nid == null) return false;
				if (usedThis.has(nid)) return false;
				if (usedElsewhere.has(nid)) return false;
				return true;
			});
		},
		[activeDevices, assignedByClass]
	);

	const confirmAddDevices = async (classId: number) => {
		const ids = Array.from(selectedOrgDeviceIds);
		if (ids.length === 0) return;

		setFormError('');
		try {
			await registerMutation.mutateAsync({
				classId,
				organizationDeviceIds: ids,
				organizationId: oid,
			});
			setAssignedByClass((prev) => ({
				...prev,
				[classId]: [...(prev[classId] ?? []), ...ids],
			}));
			onAssigned?.();
			cancelAdd();
		} catch (e) {
			setFormError(
				e instanceof Error ? e.message : t('classDeviceAllocationModal.assignError')
			);
		}
	};

	const unassignFromClass = (classId: number, orgDeviceId: number) => {
		if (!window.confirm(t('classDeviceAllocationModal.unassignFromClassConfirm'))) return;
		setFormError('');
		unassignMutation.mutate(
			{ classId, organizationDeviceIds: [orgDeviceId], organizationId: oid },
			{
				onSuccess: () => {
					setAssignedByClass((prev) => ({
						...prev,
						[classId]: (prev[classId] ?? []).filter((x) => x !== orgDeviceId),
					}));
					onAssigned?.();
				},
				onError: (e) => {
					setFormError(
						e instanceof Error ? e.message : t('classDeviceAllocationModal.unassignError')
					);
				},
			}
		);
	};

	const loading = allocationLoading;

	const footer = (
		<div className="flex justify-end gap-3">
			<Button variant="secondary" onClick={onClose}>
				{t('classDeviceAllocationModal.close')}
			</Button>
		</div>
	);

	const renderProductRow = (
		productId: number,
		productTitle?: string,
		totalDeviceCount?: number,
		classes?: Array<{
			classId: number;
			classTitle?: string;
			deviceSlotLimit?: number;
			organizationDeviceIds?: number[];
		}>
	) => {
		const isProductOpen = expandedProductIds.has(productId);
		const heading = productTitle || t('classDeviceAllocationModal.fallbackProduct', { id: productId });
		const allocatedDeviceCount = classes?.reduce(
			(sum, c) => sum + (c.organizationDeviceIds?.length ?? 0),
			0
		) ?? 0;

		return (
			<div
				key={`product-${productId}`}
				className={`rounded-xl border overflow-hidden shadow-sm transition-shadow ${
					isProductOpen
						? 'border-blue-200/80 ring-1 ring-blue-100/50 bg-white'
						: 'border-gray-200/90 bg-white hover:border-gray-300'
				}`}
			>
				<button
					type="button"
					onClick={() => toggleProduct(productId)}
					className="w-full p-4 text-left flex items-start gap-3 hover:bg-slate-50/80 transition-colors"
				>
					<span className="mt-0.5 text-gray-400 shrink-0">
						{isProductOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
					</span>
					<div className="flex-1 min-w-0">
						<div className="text-base font-semibold text-gray-900 truncate leading-snug">{heading}</div>
						<div className="text-sm text-gray-500 mt-1">
							{t('classDeviceAllocationModal.assignedOfTotal', { 
								assigned: allocatedDeviceCount, 
								total: totalDeviceCount ?? 0 
							})}
						</div>
					</div>
				</button>

				{isProductOpen && classes && classes.length > 0 && (
					<div className="border-t border-gray-200 bg-slate-50/40 p-3 space-y-2">
						{classes.map((c) =>
							renderClassRow(c.classId, c.classTitle, `p${productId}-c${c.classId}`, c.deviceSlotLimit)
						)}
					</div>
				)}

				{isProductOpen && (!classes || classes.length === 0) && (
					<div className="border-t border-gray-200 bg-slate-50/40 p-4">
						<p className="text-sm text-gray-500 text-center">
							{t('classDeviceAllocationModal.noClassesInProduct')}
						</p>
					</div>
				)}
			</div>
		);
	};

	const renderClassRow = (classId: number, classTitle?: string, listKey?: string, deviceSlotLimit?: number) => {
		const isClassOpen = expandedClassIds.has(classId);
		const assigned = assignedByClass[classId] ?? [];
		const isAdding = addingClassId === classId;
		const eligible = eligibleDevicesForClass(classId);
		const heading =
			(classTitle && classTitle.trim()) || t('classDeviceAllocationModal.fallbackClass', { id: classId });

		return (
			<div
				key={listKey ?? String(classId)}
				className={`rounded-xl border overflow-hidden shadow-sm transition-shadow ${
					isClassOpen
						? 'border-sky-200/80 ring-1 ring-sky-100/50 bg-white'
						: 'border-gray-200/90 bg-white hover:border-gray-300'
				}`}
			>
				<button
					type="button"
					onClick={() => toggleClass(classId)}
					className="w-full p-4 text-left flex items-start gap-3 hover:bg-slate-50/80 transition-colors"
				>
					<span className="mt-0.5 text-gray-400 shrink-0">
						{isClassOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
					</span>
					<div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center shrink-0">
						<GraduationCap size={20} />
					</div>
					<div className="flex-1 min-w-0">
						<div className="text-base font-semibold text-gray-900 truncate leading-snug">{heading}</div>
						<div className="text-sm text-gray-500 mt-1">
							{deviceSlotLimit != null && deviceSlotLimit > 0
								? t('classDeviceAllocationModal.assignedOfTotal', {
										assigned: assigned.length,
										total: deviceSlotLimit,
									})
								: t('classDeviceAllocationModal.assignedCount', { count: assigned.length })}
						</div>
					</div>
				</button>

				{isClassOpen && (
					<div className="border-t border-gray-200 bg-slate-50/70 p-4 space-y-4">
						{formError && (
							<p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
								{formError}
							</p>
						)}
						{isAdding ? (
							<div className="space-y-2">
								<div className="flex items-center justify-between gap-2">
									<span className="text-sm font-medium text-gray-800">
										{t('classDeviceAllocationModal.selectDevices')}
									</span>
									<div className="flex gap-2">
										<Button variant="secondary" size="sm" onClick={cancelAdd} type="button">
											{t('classDeviceAllocationModal.cancel')}
										</Button>
										<Button
											variant="primary"
											size="sm"
											type="button"
											disabled={selectedOrgDeviceIds.size === 0 || registerMutation.isPending}
											onClick={() => void confirmAddDevices(classId)}
										>
											{registerMutation.isPending
												? t('classDeviceAllocationModal.saving')
												: t('classDeviceAllocationModal.assignWithCount', {
														count: selectedOrgDeviceIds.size,
													})}
										</Button>
									</div>
								</div>
								<div className="max-h-52 overflow-y-auto space-y-2">
									{eligible.length > 0 ? (
										eligible
											.map((d) => ({
												d,
												nid: parseUiDeviceIdToApiOrganizationDeviceId(String(d.id)),
											}))
											.filter((o): o is { d: Device; nid: number } => o.nid != null)
											.map(({ d, nid }) => {
											const selected = selectedOrgDeviceIds.has(nid);
											return (
												<button
													key={d.id}
													type="button"
													onClick={() => toggleDeviceSelect(nid)}
													className={`w-full p-2.5 rounded-lg border text-left flex items-center gap-3 transition-colors ${
														selected
															? 'border-sky-500 bg-sky-50'
															: 'border-gray-200 bg-white hover:border-sky-200'
													}`}
												>
													<span
														className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
															selected ? 'border-sky-600 bg-sky-600' : 'border-gray-300'
														}`}
													>
														{selected && <Check size={12} className="text-white" />}
													</span>
													<Monitor size={18} className="text-gray-500 shrink-0" />
													<div className="min-w-0">
														<div className="text-sm font-semibold text-gray-900 truncate">
															{d.deviceName ?? d.name ?? `기기 #${d.id}`}
														</div>
														<div className="text-xs text-gray-500">{d.modelName}</div>
													</div>
												</button>
											);
										})
									) : (
										<p className="text-sm text-gray-500 text-center py-3">
											{t('classDeviceAllocationModal.noEligibleDevices')}
										</p>
									)}
								</div>
							</div>
						) : (
							<div className="space-y-3">
								<div>
									<h4 className="text-sm font-bold text-gray-800 mb-2">
										{t('classDeviceAllocationModal.assignedListHeading')}
									</h4>
									{assigned.length > 0 ? (
										<ul className="space-y-2">
											{assigned.map((orgId) => {
												const dev = findDeviceByOrgId(devices, orgId);
												const title = dev?.deviceName ?? dev?.name ?? `기기 #${orgId}`;
												return (
													<li
														key={orgId}
														className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
													>
														<div className="flex min-w-0 items-center gap-3 flex-1">
															<Monitor size={20} className="text-gray-500 shrink-0" />
															<div className="min-w-0 text-left">
																<div className="text-sm font-semibold text-gray-900 truncate">
																	{title}
																</div>
																{dev?.modelName ? (
																	<div className="text-xs text-gray-500 truncate">{dev.modelName}</div>
																) : null}
															</div>
														</div>
														<button
															type="button"
															aria-label={t('classDeviceAllocationModal.unassignButtonAria')}
															disabled={unassignMutation.isPending}
															onClick={() => unassignFromClass(classId, orgId)}
															className="p-1.5 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
														>
															<X size={18} strokeWidth={2.2} />
														</button>
													</li>
												);
											})}
										</ul>
									) : (
										<p className="text-sm text-gray-500 text-center py-1">
											{t('classDeviceAllocationModal.emptyAssignedInClass')}
										</p>
									)}
								</div>

								{eligible.length > 0 ? (
									<button
										type="button"
										onClick={() => startAddDevices(classId)}
										className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-gray-700 border-2 border-dashed border-gray-300 rounded-xl bg-white hover:border-sky-400/80 hover:text-sky-800 hover:bg-sky-50/50 transition-colors"
									>
										{t('classDeviceAllocationModal.addDevices')}
									</button>
								) : (
									<p className="text-xs text-gray-500 text-center">
										{t('classDeviceAllocationModal.noEligibleDevicesShort')}
									</p>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t('classDeviceAllocationModal.title')}
			description={t('classDeviceAllocationModal.subtitle')}
			footer={footer}
			size="large"
		>
			<div className="space-y-3">
				{enabled && loading && (
					<div className="border border-dashed border-gray-200 bg-gray-50/80 py-10 text-center rounded-lg">
						<p className="text-sm text-gray-500">{t('classDeviceAllocationModal.loading')}</p>
					</div>
				)}

				{enabled && !loading && classIds.length === 0 && !hasProductTree && (
					<div className="border border-gray-200 bg-white px-4 py-10 text-center rounded-lg">
						<p className="text-sm text-gray-500">{t('classDeviceAllocationModal.empty')}</p>
					</div>
				)}

				{enabled && !loading && hasProductTree && (
					<div className="space-y-2 max-h-[min(70vh,560px)] overflow-y-auto pr-1 -mt-1">
						{productGroups && productGroups.length > 0 ? (
							<div className="space-y-2">
								{productGroups.map((pg) =>
									renderProductRow(pg.productId, pg.productTitle, pg.totalDeviceCount, pg.classes)
								)}
							</div>
						) : (
							<div className="border border-sky-100/90 bg-sky-50/60 rounded-xl px-4 py-8 text-center">
								<p className="text-sm text-slate-800 leading-relaxed max-w-md mx-auto">
									{t('classDeviceAllocationModal.emptyNoProductRegistered')}
								</p>
							</div>
						)}
					</div>
				)}

				{enabled && !loading && !hasProductTree && classIds.length > 0 && (
					<div className="space-y-2 max-h-[min(70vh,560px)] overflow-y-auto pr-1 -mt-1">
						{classIds.map((cid) => renderClassRow(cid, undefined, `c-${cid}`))}
					</div>
				)}
			</div>
		</Modal>
	);
}
